import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, CircleNotch, Plant as PlantIcon, ClockCountdown } from "@phosphor-icons/react";
import { createEvent, fileUrl } from "../lib/api";
import { REMINDER_META, STATUS_COLOR, isActionable, whenText } from "../lib/reminders";

export function ReminderRow({ r, onDone, showPlant = true }) {
  const [busy, setBusy] = useState(false);
  const m = REMINDER_META[r.event_type];
  const done = async () => {
    setBusy(true);
    try {
      await createEvent(r.plant_id, { event_type: r.event_type, notes: "Logged from care reminder" });
      toast.success(`${m.label} logged for ${r.plant?.nickname}`);
      onDone?.();
    } catch {
      toast.error("Could not log care");
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="flex items-center gap-3 py-3" data-testid={`reminder-${r.plant_id}-${r.event_type}`}>
      {showPlant && (
        <span className="h-10 w-10 rounded-xl overflow-hidden bg-muted shrink-0 flex items-center justify-center">
          {r.plant?.photo_path ? <img loading="lazy" decoding="async" src={fileUrl(r.plant.photo_path, 160)} alt="" className="h-full w-full object-cover" /> : <PlantIcon weight="duotone" className="h-4 w-4 text-muted-foreground" />}
        </span>
      )}
      <span className="h-8 w-8 rounded-full flex items-center justify-center shrink-0" style={{ background: `${m.color}14`, color: m.color }}>
        <m.Icon weight="duotone" className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        {showPlant ? (
          <Link to={`/plants/${r.plant_id}`} className="text-sm font-medium truncate block hover:underline underline-offset-4">{r.plant?.nickname}</Link>
        ) : (
          <p className="text-sm font-medium">{m.label}</p>
        )}
        <p className="text-xs text-muted-foreground sm:truncate">
          <span className="sm:hidden font-medium" style={{ color: STATUS_COLOR[r.status] }}>{whenText(r)} · </span>
          {showPlant && `${m.label} · `}every ~{r.interval_days}d{r.dormant ? " (dormant pace)" : ""} · {r.last_at ? `last ${Math.max(0, r.interval_days - r.days_until)}d ago` : "never logged"}
        </p>
      </div>
      <span className="hidden sm:inline text-xs font-medium whitespace-nowrap" style={{ color: STATUS_COLOR[r.status] }} data-testid={`reminder-when-${r.plant_id}-${r.event_type}`}>{whenText(r)}</span>
      <button type="button" disabled={busy} onClick={done} data-testid={`reminder-done-${r.plant_id}-${r.event_type}`}
        className="h-8 px-3 rounded-full bg-muted text-xs font-medium inline-flex items-center gap-1 hover:bg-muted/70 active:scale-[0.97] transition-[background-color,transform]">
        {busy ? <CircleNotch className="h-3.5 w-3.5 animate-spin" /> : <Check weight="bold" className="h-3.5 w-3.5" />} Done
      </button>
    </li>
  );
}

export default function DueSoon({ reminders, onChange }) {
  const [all, setAll] = useState(false);
  const due = reminders.filter(isActionable);
  const overdue = due.filter((r) => r.status === "overdue").length;
  const shown = all ? due : due.slice(0, 6);
  return (
    <section className="mb-10 rounded-3xl bg-card p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04)]" data-testid="due-soon-section">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center"><ClockCountdown weight="duotone" className="h-5 w-5 text-primary" /></span>
          <div>
            <h2 className="text-base md:text-lg font-semibold tracking-tight">Care due soon</h2>
            <p className="text-xs text-muted-foreground" data-testid="due-soon-summary">
              {due.length ? `${due.length} reminder${due.length !== 1 ? "s" : ""}${overdue ? ` · ${overdue} overdue` : ""} · based on each plant's last care and schedule` : "All caught up. Nothing is due in the next few days."}
            </p>
          </div>
        </div>
        {due.length > 6 && (
          <button data-testid="due-soon-toggle" onClick={() => setAll((v) => !v)} className="text-sm text-muted-foreground hover:text-foreground">{all ? "Show less" : `Show all ${due.length}`}</button>
        )}
      </div>
      {shown.length > 0 && <ul className="mt-2 divide-y divide-border/60">{shown.map((r) => <ReminderRow key={`${r.plant_id}-${r.event_type}`} r={r} onDone={onChange} />)}</ul>}
    </section>
  );
}
